<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.vehicle.label', 2), to: '/vehicles' },
      { label: 'Offers', to: '/vehicles/offers' }
    ]"
  />

  <div>
    <div class="mb-6 flex flex-wrap items-center gap-4">
      <div class="min-w-64">
        <Label for="vehicle-offer-condition-filter">Condition</Label>
        <Select
          id="vehicle-offer-condition-filter"
          v-model="selectedVehicleConditionModel"
          :options="vehicleConditionOptions"
          mode="single"
          searchable
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="All Conditions"
          clearable
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </div>

      <div class="min-w-64">
        <Label for="vehicle-offer-status-filter">Status</Label>
        <Select
          id="vehicle-offer-status-filter"
          v-model="selectedOfferStatusModel"
          :options="offerStatusOptions"
          mode="single"
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="All Statuses"
          clearable
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </div>

      <div class="flex-1" />

      <Button
        v-if="hasActiveFilters"
        :label="$t('ui.actions.clear', 1)"
        severity="secondary"
        type="button"
        outlined
        icon="filter-slash"
        @click="clearFilters"
      />
    </div>

    <div
      v-if="isLoading"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
    >
      <div
        v-for="index in 6"
        :key="index"
        class="border border-border-secondary rounded-xl bg-quaternary overflow-hidden p-4"
      >
        <div class="flex flex-col gap-3">
          <Skeleton
            height="1.5rem"
            width="80%"
          />
          <Skeleton
            height="1rem"
            width="50%"
          />
          <div class="flex gap-2">
            <Skeleton
              height="1.5rem"
              width="5rem"
            />
            <Skeleton
              height="1.5rem"
              width="4rem"
            />
          </div>
          <Skeleton
            height="5rem"
            width="100%"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="filteredOffers.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
    >
      <VehicleOfferCard
        v-for="offer in filteredOffers"
        :key="offer.id"
        :offer="offer"
      />
    </div>

    <Empty
      v-else
      title="No vehicle offers found"
      description="Try adjusting your filters or check back later."
    />
  </div>
</template>

<script setup lang="ts">
// libraries
import { computed, onMounted, ref } from 'vue'

// components
import Button from '@/components/button/Button.vue'
import Empty from '@/components/empty/Empty.vue'
import Label from '@/components/forms/Label.vue'
import Select from '@/components/forms/Select.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import VehicleOfferCard from '@/components/unique/vehicles/VehicleOfferCard.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// types
import type { VehicleOffer, VehicleOfferCondition } from '@/types/domain/vehicleOffer'

// utilities
import { alertToast } from '@/utilities/notification'

type OfferStatusFilter = 'active' | 'archived'

const offers = ref<VehicleOffer[]>([])
const selectedVehicleCondition = ref<VehicleOfferCondition | null>(null)
const selectedOfferStatus = ref<OfferStatusFilter | null>(null)

const offersApiRequest = useApiRequest()
const isLoading = offersApiRequest.isLoading

/**
 * Available vehicle condition filter options.
 */
const vehicleConditionOptions = [
  { value: 'all', label: 'All Vehicles' },
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' }
]

/**
 * Available offer status filter options.
 */
const offerStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' }
]

/**
 * Returns true when any browser filter is currently active.
 */
const hasActiveFilters = computed<boolean>(() => {
  return selectedVehicleCondition.value !== null || selectedOfferStatus.value !== null
})

/**
 * Filters offers based on the selected condition and archive status.
 */
const filteredOffers = computed<VehicleOffer[]>(() => {
  return offers.value.filter(offer => {
    const matchesCondition =
      selectedVehicleCondition.value === null || offer.vehicleCondition === selectedVehicleCondition.value

    const matchesStatus =
      selectedOfferStatus.value === null ||
      (selectedOfferStatus.value === 'archived' ? offer.isArchived : !offer.isArchived)

    return matchesCondition && matchesStatus
  })
})

/**
 * Select-friendly vehicle condition model for the browser filters.
 */
const selectedVehicleConditionModel = computed<VehicleOfferCondition | undefined>({
  get() {
    return selectedVehicleCondition.value ?? undefined
  },
  set(value) {
    selectedVehicleCondition.value = value ?? null
  }
})

/**
 * Select-friendly offer status model for the browser filters.
 */
const selectedOfferStatusModel = computed<OfferStatusFilter | undefined>({
  get() {
    return selectedOfferStatus.value ?? undefined
  },
  set(value) {
    selectedOfferStatus.value = value ?? null
  }
})

/**
 * Loads the current vehicle offers list for the browser view.
 */
async function fetchOffers(): Promise<void> {
  try {
    const response = await offersApiRequest.send<VehicleOffer[]>({
      endpoint: '/v1/vehicle-offers',
      method: 'GET',
      params: {
        includeArchived: true
      }
    })

    if (response.success && Array.isArray(response.data)) {
      offers.value = response.data
      return
    }

    alertToast('Error', response.message || 'Failed to fetch vehicle offers', 'error')
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch vehicle offers'
    alertToast('Error', message, 'error')
  }
}

/**
 * Resets all browser filters back to their defaults.
 */
function clearFilters(): void {
  selectedVehicleCondition.value = null
  selectedOfferStatus.value = null
}

onMounted(() => {
  void fetchOffers()
})
</script>

<style scoped></style>
